import React, { useCallback, useEffect, useState } from 'react';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { accountsAPI } from '../services/api';
import { getUser } from '../services/auth';
import { cacheUtils } from '../utils/cache';
import { resolveApiErrorMessage } from '../utils/apiErrors';
import { ArrowDownRight, ArrowUpRight, RefreshCw, ShieldCheck, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useLocalization } from '../context/LocalizationContext';

const EMPTY_GROUPS = [];

const parseApiValue = (payload) => {
  if (!payload) return {};
  if (payload.value && typeof payload.value === 'object') return payload.value;
  if (typeof payload.value === 'string') {
    try {
      const parsed = JSON.parse(payload.value);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (_e) {
      return {};
    }
  }
  return payload && typeof payload === 'object' ? payload : {};
};

const normalizeSuretyRecord = (item = {}, fallbackLoanTypeCode = '', index = 0) => ({
  id: `${fallbackLoanTypeCode || item.loanType || 'loan'}-${item.loanNumber || item.memberGl || index}`,
  loanTypeCode: String(fallbackLoanTypeCode || item.loanTypeCode || item.loanType || '').trim(),
  memberName: String(item.memberName || '').trim() || 'N/A',
  memberGl: String(item.memberGl || '').trim() || 'N/A',
  employeeId: String(item.employeeId || '').trim() || 'N/A',
  loanNumber: String(item.loanNumber || '').trim() || 'N/A',
  loanType: String(item.loanType || item.loanTypeDesc || fallbackLoanTypeCode || '').trim() || 'N/A',
  loanDate: item.loanDate || null,
  repayTenure: Number.isFinite(Number(item.repayTenure)) ? Number(item.repayTenure) : null,
});

const toGroups = (data) => {
  const value = parseApiValue(data);
  if (!value || typeof value !== 'object') return EMPTY_GROUPS;

  if (Array.isArray(value)) {
    const groups = new Map();
    value.forEach((item, index) => {
      const normalized = normalizeSuretyRecord(item, '', index);
      const groupKey = normalized.loanType || 'Loan';
      if (!groups.has(groupKey)) {
        groups.set(groupKey, {
          key: groupKey,
          loanTypeCode: '',
          title: groupKey,
          records: [],
        });
      }
      groups.get(groupKey).records.push(normalized);
    });

    return Array.from(groups.values())
      .filter((group) => group.records.length > 0)
      .sort((a, b) => a.title.localeCompare(b.title));
  }

  return Object.entries(value)
    .map(([loanTypeCode, list]) => {
      const records = Array.isArray(list) ? list : [];
      const normalized = records.map((item, index) => normalizeSuretyRecord(item, loanTypeCode, index));

      return {
        key: loanTypeCode || normalized[0]?.loanType || 'UNKNOWN',
        loanTypeCode: loanTypeCode || 'UNKNOWN',
        title: normalized[0]?.loanType || loanTypeCode || 'Loan',
        records: normalized,
      };
    })
    .filter((group) => group.records.length > 0)
    .sort((a, b) => a.loanTypeCode.localeCompare(b.loanTypeCode));
};

const formatDate = (dateValue, locale = 'en-IN') => {
  if (!dateValue) return 'N/A';
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
};

const countRecords = (groups) => groups.reduce((sum, group) => sum + group.records.length, 0);

const groupRecordsByLoan = (records = []) => {
  const buckets = new Map();
  records.forEach((record) => {
    const key = `${record.loanNumber}|${record.loanDate || ''}|${record.repayTenure ?? ''}`;
    if (!buckets.has(key)) {
      buckets.set(key, {
        loanNumber: record.loanNumber,
        loanDate: record.loanDate,
        repayTenure: record.repayTenure,
        members: [],
      });
    }
    buckets.get(key).members.push(record);
  });
  return Array.from(buckets.values());
};

export const SuretyEnquiry = () => {
  const { needsCompactFont, locale, t } = useLocalization();
  const user = getUser() || {};
  const memberGl = String(user.memberGl || user.customerId || user.username || '').trim();
  const CACHE_KEY = `surety_enquiry_${memberGl}`;

  const [activeTab, setActiveTab] = useState('taken_from');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [takenFromGroups, setTakenFromGroups] = useState(EMPTY_GROUPS);
  const [givenToGroups, setGivenToGroups] = useState(EMPTY_GROUPS);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchSuretyData = useCallback(
    async (showLoading = true) => {
      if (!memberGl) {
        setErrorMessage(t('surety.memberUnavailable'));
        setLoading(false);
        return;
      }

      if (showLoading) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      setErrorMessage('');
      try {
        const [takenFromResponse, givenToResponse] = await Promise.all([
          accountsAPI.getSuretyTakenFrom(memberGl),
          accountsAPI.getSuretyGivenTo(memberGl),
        ]);

        const nextTakenFrom = toGroups(takenFromResponse?.data);
        const nextGivenTo = toGroups(givenToResponse?.data);
        setTakenFromGroups(nextTakenFrom);
        setGivenToGroups(nextGivenTo);
        cacheUtils.setCache(CACHE_KEY, {
          takenFromGroups: nextTakenFrom,
          givenToGroups: nextGivenTo,
        });
        setLastUpdated(Date.now());
        if (!showLoading) {
          toast.success(t('surety.refreshSuccess'));
        }
      } catch (error) {
        const message = resolveApiErrorMessage(error, t, t('surety.failedToLoad'));
        const cached = cacheUtils.getCache(CACHE_KEY);
        const hasCachedData =
          (cached && Array.isArray(cached.takenFromGroups) && cached.takenFromGroups.length > 0) ||
          (cached && Array.isArray(cached.givenToGroups) && cached.givenToGroups.length > 0);
        if (hasCachedData) {
          setErrorMessage(t('surety.refreshFailedCached'));
          toast.error(message);
        } else {
          setErrorMessage(message);
          toast.error(message);
        }
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [CACHE_KEY, memberGl, t]
  );

  useEffect(() => {
    const cached = cacheUtils.getCache(CACHE_KEY);
    if (cached && typeof cached === 'object') {
      setTakenFromGroups(Array.isArray(cached.takenFromGroups) ? cached.takenFromGroups : EMPTY_GROUPS);
      setGivenToGroups(Array.isArray(cached.givenToGroups) ? cached.givenToGroups : EMPTY_GROUPS);
      setLastUpdated(cacheUtils.getCacheTimestamp(CACHE_KEY));
      setLoading(false);
      return;
    }
    fetchSuretyData(true);
  }, [CACHE_KEY, fetchSuretyData]);

  const activeGroups = activeTab === 'taken_from' ? takenFromGroups : givenToGroups;
  const takenFromCount = countRecords(takenFromGroups);
  const givenToCount = countRecords(givenToGroups);

  return (
    <DashboardLayout>
      <div className="app-page-shell max-w-5xl fade-in" data-testid="surety-enquiry-page">
        <div className="pointer-events-none absolute -z-10 inset-0 dark:hidden">
          <div className="absolute -top-10 -left-8 h-44 w-44 rounded-full bg-primary-200/35 blur-3xl" />
          <div className="absolute top-24 right-0 h-48 w-48 rounded-full bg-primary-200/30 blur-3xl" />
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1
              className={`app-page-heading ${needsCompactFont ? 'app-page-heading-te' : ''}`}
            >
              {t('surety.title')}
            </h1>
            {lastUpdated && (
              <p className="app-page-meta">
                {t('common.lastUpdated', { time: cacheUtils.getTimeAgo(lastUpdated, locale, t) })}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => {
              cacheUtils.clearCache(CACHE_KEY);
              fetchSuretyData(false);
            }}
            disabled={refreshing || loading}
            className="app-primary-button"
          >
            <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
            <span>{refreshing ? t('common.refreshing') : t('common.refresh')}</span>
          </button>
        </div>

        <div className="app-hero-surface p-4 sm:p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="font-serif text-2xl sm:text-3xl font-bold">{t('surety.relationships')}</h2>
            </div>
            <div className="bg-white/20 backdrop-blur-md p-3 rounded-xl border border-white/30">
              <ShieldCheck size={28} strokeWidth={1.5} />
            </div>
          </div>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
            <button
              type="button"
              onClick={() => setActiveTab('taken_from')}
              className={`rounded-xl p-3 text-left border transition-all ${
                activeTab === 'taken_from'
                  ? 'bg-white/30 border-white/50'
                  : 'bg-white/10 border-white/20 hover:bg-white/20'
              }`}
              data-testid="surety-tab-taken-from"
            >
                <div className="flex items-center gap-2">
                  <ArrowDownRight size={18} />
                <p className="text-sm text-white/85">{t('surety.takenFrom')}</p>
                </div>
              <p className="font-serif text-xl font-bold mt-1">{takenFromCount}</p>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab('given_to')}
              className={`rounded-xl p-3 text-left border transition-all ${
                activeTab === 'given_to'
                  ? 'bg-white/30 border-white/50'
                  : 'bg-white/10 border-white/20 hover:bg-white/20'
              }`}
              data-testid="surety-tab-given-to"
            >
                <div className="flex items-center gap-2">
                  <ArrowUpRight size={18} />
                <p className="text-sm text-white/85">{t('surety.givenTo')}</p>
                </div>
              <p className="font-serif text-xl font-bold mt-1">{givenToCount}</p>
            </button>
          </div>
        </div>

        {!!errorMessage && !loading && (
          <div className="rounded-xl border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 px-4 py-3">
            <p className="text-sm text-red-700 dark:text-red-200">{errorMessage}</p>
          </div>
        )}

        {loading ? (
          <div className="app-surface p-8">
            <div className="flex items-center justify-center gap-3">
              <RefreshCw size={20} className="animate-spin text-primary-500" />
              <span className="text-sm text-primary-800 dark:text-primary-200">{t('surety.loading')}</span>
            </div>
          </div>
        ) : activeGroups.length === 0 ? (
          <div className="app-surface p-10 text-center">
            <Users size={42} className="mx-auto text-primary-500 dark:text-primary-400 mb-3" />
            <h3 className="font-serif text-2xl font-semibold text-primary-900 dark:text-white mb-1.5">
              {t('common.noRecordsFound')}
            </h3>
            <p className="text-sm text-primary-700 dark:text-primary-300">
              {activeTab === 'taken_from'
                ? t('surety.noTakenFrom')
                : t('surety.noGivenTo')}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {activeGroups.map((group) => (
              <div
                key={`${activeTab}-${group.key}`}
                className="app-surface overflow-hidden"
              >
                <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3 border-b border-primary-200 dark:border-slate-800 bg-primary-50/80 dark:bg-slate-900/70">
                  <div>
                    <h3 className="font-serif text-lg sm:text-xl font-semibold text-primary-900 dark:text-white">
                      {group.loanTypeCode ? `${group.title} (${group.loanTypeCode})` : group.title}
                    </h3>
                  </div>
                  <span className="text-xs sm:text-sm font-semibold px-3 py-1 rounded-full bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 border border-primary-100 dark:border-primary-900/40">
                    {group.records.length} {t('common.members')}
                  </span>
                </div>

                <div className="space-y-4 p-4 sm:p-5">
                  {groupRecordsByLoan(group.records).map((loan) => (
                    <div
                      key={`${group.loanTypeCode}-${loan.loanNumber}-${loan.loanDate || ''}`}
                      className="rounded-xl border border-primary-200 dark:border-slate-800 overflow-hidden"
                    >
                      <div className="px-4 py-3 bg-primary-50 dark:bg-slate-900/60 border-b border-primary-200 dark:border-slate-800">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
                          <p className="text-primary-800 dark:text-primary-200">
                            <span className="font-semibold">{t('surety.loanNo')}:</span> {loan.loanNumber}
                          </p>
                          <p className="text-primary-800 dark:text-primary-200">
                            <span className="font-semibold">{t('surety.loanDate')}:</span> {formatDate(loan.loanDate, locale)}
                          </p>
                          <p className="text-primary-800 dark:text-primary-200">
                            <span className="font-semibold">{t('surety.tenure')}:</span> {loan.repayTenure ?? 'N/A'} {t('surety.months', {}, 'months')}
                          </p>
                        </div>
                      </div>
                      <div className="sm:hidden divide-y divide-primary-200 dark:divide-slate-800">
                        {loan.members.map((record) => (
                          <div key={record.id} className="px-4 py-3 space-y-2">
                            <p className="text-sm font-semibold text-primary-900 dark:text-white">
                              {record.memberName}
                            </p>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <p className="text-primary-600 dark:text-primary-300">{t('surety.memberGl')}</p>
                              <p className="text-primary-800 dark:text-primary-200 font-medium">{record.memberGl}</p>
                              <p className="text-primary-600 dark:text-primary-300">{t('surety.employeeId')}</p>
                              <p className="text-primary-800 dark:text-primary-200 font-medium">{record.employeeId}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="hidden sm:block overflow-x-auto">
                        <table className="w-full min-w-[640px]">
                          <thead className="bg-primary-100/80 dark:bg-slate-800/70">
                            <tr>
                              <th className="px-4 py-2.5 whitespace-nowrap text-left text-xs font-bold text-primary-800 dark:text-primary-200 uppercase tracking-wider">
                                {t('surety.memberName')}
                              </th>
                              <th className="px-4 py-2.5 whitespace-nowrap text-left text-xs font-bold text-primary-800 dark:text-primary-200 uppercase tracking-wider">
                                {t('surety.memberGl')}
                              </th>
                              <th className="px-4 py-2.5 whitespace-nowrap text-left text-xs font-bold text-primary-800 dark:text-primary-200 uppercase tracking-wider">
                                {t('surety.employeeId')}
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-primary-200 dark:divide-slate-800">
                            {loan.members.map((record) => (
                              <tr key={record.id} className="hover:bg-primary-50/60 dark:hover:bg-slate-800/40">
                                <td className="px-4 py-2.5 whitespace-nowrap text-sm text-primary-900 dark:text-white font-medium">
                                  {record.memberName}
                                </td>
                                <td className="px-4 py-2.5 whitespace-nowrap text-sm text-primary-800 dark:text-primary-200">
                                  {record.memberGl}
                                </td>
                                <td className="px-4 py-2.5 whitespace-nowrap text-sm text-primary-800 dark:text-primary-200">
                                  {record.employeeId}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default SuretyEnquiry;
